import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarClock, CircleCheck, ExternalLink, EyeOff, TriangleAlert } from 'lucide-react'
import { toast } from 'sonner'
import { api, ApiError } from '@/lib/api'
import type { CaseDetail, CaseParticipant } from '@/lib/api-types'
import { formatDate, relativeTime } from '@/lib/format'
import { priorityLabelFor } from '@/lib/priority'
import { renderMarkdown } from '@/lib/sanitize'
import { useSessionStore } from '@/stores/session'
import { Button } from '@/components/ui/button'
import { SensitiveChip } from '@/components/ui/sensitive-chip'
import { Spinner } from '@/components/ui/spinner'
import { StatusChip } from '@/components/ui/status-chip'
import { CasePath } from './case-path'
import { CommentComposer } from './comment-composer'
import { FileChip } from './file-chip'
import { ParticipantPicker } from './participant-picker'
import { ScheduleCallDialog } from './schedule-call-dialog'
import { Timeline } from './timeline'

export function CaseDetailPage() {
  const { id = '' } = useParams()

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['case', id],
    queryFn: ({ signal }) => api.getCase(id, { signal }),
    staleTime: 15_000,
    // Fresh replies matter most on an open case page — poll gently while
    // the tab is visible (TanStack pauses the interval in background tabs).
    refetchInterval: 60_000,
  })

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner label="Loading this case…" />
      </div>
    )
  }

  if (error) {
    const missing = error instanceof ApiError && error.status === 404
    return (
      <div className="py-24 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.16em] text-mute">
          {missing ? 'Not found' : 'Something went wrong'}
        </p>
        <p className="mt-3 text-[0.9375rem] text-inkMid">
          {missing
            ? 'This case doesn’t exist — or it belongs to another account.'
            : 'We couldn’t load this case.'}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          {!missing ? (
            <Button variant="neutral" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          ) : null}
          <Button asChild variant="quiet" size="sm">
            <Link to="/cases">Back to cases</Link>
          </Button>
        </div>
      </div>
    )
  }

  return <CaseDetailView detail={data} />
}

function CaseDetailView({ detail }: { detail: CaseDetail }) {
  const accountName = useSessionStore((s) => s.contact?.accountName)
  return (
    <article>
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="flex items-baseline gap-2 font-mono text-[0.8125rem]">
        <Link
          to="/cases"
          className="rounded-sm text-mute underline-offset-4 transition-colors duration-[180ms] ease-editorial hover:text-crimson hover:underline"
        >
          Cases
        </Link>
        <span aria-hidden="true" className="text-muteSoft">
          ·
        </span>
        <span aria-current="page" className="text-inkMid">
          #{detail.caseNumber}
        </span>
      </nav>

      <h1 className="mt-3 max-w-prose text-[1.75rem] font-semibold leading-snug tracking-[-0.012em] text-ink">
        {detail.subject}
      </h1>

      {/* Meta row */}
      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-[0.8125rem] text-mute">
        <StatusChip status={detail.status} />
        {detail.sensitive ? <SensitiveChip /> : null}
        <MetaDot />
        <span className="text-inkMid">{detail.recordTypeLabel}</span>
        <MetaDot />
        <span>
          Created <time dateTime={detail.createdAt}>{formatDate(detail.createdAt)}</time>
        </span>
        <MetaDot />
        <span>
          Updated{' '}
          <time dateTime={detail.lastModifiedAt} className="font-mono">
            {relativeTime(detail.lastModifiedAt)}
          </time>
        </span>
        {detail.urgency ? (
          <>
            <MetaDot />
            <span>
              Urgency <span className="text-inkMid">{detail.urgency}</span>
            </span>
          </>
        ) : null}
        {priorityLabelFor(detail.priority) ? (
          <>
            <MetaDot />
            <span>
              Priority <span className="text-inkMid">{priorityLabelFor(detail.priority)}</span>
            </span>
          </>
        ) : null}
        {!detail.owner.isQueue ? (
          <>
            <MetaDot />
            <span className="text-inkMid">With {detail.owner.name}</span>
          </>
        ) : null}
        {detail.submittedBy ? (
          <>
            <MetaDot />
            <span>Submitted by {detail.submittedBy.name}</span>
          </>
        ) : null}
      </div>

      <CasePath status={detail.status} recordType={detail.recordType} />

      {detail.waitingOnYou ? (
        <div
          role="status"
          className="mt-6 flex items-start gap-3 rounded-lg border border-amber/40 bg-amber/10 px-4 py-3.5"
        >
          <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-amber" />
          <div>
            <p className="text-sm font-semibold text-ink">Waiting on you</p>
            <p className="mt-0.5 text-sm leading-relaxed text-inkMid">
              RevenuePoint needs something from you before this can move. Check the latest note below
              and reply when you&rsquo;re ready.
            </p>
          </div>
        </div>
      ) : null}

      {detail.sensitive ? (
        <div
          role="note"
          className="mt-6 flex items-start gap-3 rounded-lg border border-navy/30 bg-navyTint/50 px-4 py-3.5"
        >
          <EyeOff aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-navy" />
          <div>
            <p className="text-sm font-semibold text-ink">Sensitive case</p>
            <p className="mt-0.5 text-sm leading-relaxed text-inkMid">
              Only you, the participants on this case, and RevenuePoint can see it. It&rsquo;s
              hidden from everyone else at {accountName ?? 'your account'}.
            </p>
          </div>
        </div>
      ) : null}

      {/* Description + right rail (Participants, Files) — two columns on wide
          screens. Description is client-authored Markdown, sanitized before render. */}
      <div className="mt-8 lg:grid lg:grid-cols-[minmax(0,1fr)_260px] lg:items-start lg:gap-10">
        <section aria-labelledby="case-description-heading">
          <h2 id="case-description-heading" className="micro-label">
            Description
          </h2>
          <CaseDescription markdown={detail.description} />
        </section>

        <div className="mt-8 space-y-8 lg:sticky lg:top-6 lg:mt-0">
          <ParticipantsPanel detail={detail} />

          <ScheduleCallPanel detail={detail} />

          {detail.files.length > 0 ? (
            <section aria-labelledby="case-files-heading">
              <h2 id="case-files-heading" className="micro-label">
                Files
              </h2>
              <ul className="mt-2.5 flex flex-wrap gap-2 lg:flex-col">
                {detail.files.map((file) => (
                  <li key={file.contentDocumentId}>
                    <FileChip caseId={detail.id} file={file} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </div>

      {/* Composer + resolve lead the activity block, so the newest entries
          (feed is newest-first) sit right under the reply box. */}
      <section
        aria-labelledby="case-activity-heading"
        className="mt-10 border-t border-rule/50 pt-8"
      >
        <h2 id="case-activity-heading" className="micro-label">
          Activity
        </h2>
        <ComposerSection detail={detail} />
        <div className="mt-8">
          <Timeline entries={detail.timeline} caseId={detail.id} />
        </div>
      </section>
    </article>
  )
}

function ComposerSection({ detail }: { detail: CaseDetail }) {
  // Impersonated sessions write too — the API attributes their entries to
  // the actor and renders them RevenuePoint-side. Sits inside the Activity
  // section (above the feed), so no divider of its own.
  return (
    <div className="mt-5">
      <CommentComposer caseId={detail.id} />
      {detail.statusGroup === 'open' ? <ResolveRow caseId={detail.id} /> : null}
    </div>
  )
}

/**
 * Participants (CC'd colleagues) — see, add, remove. Add/remove hit the API and
 * optimistically update the cached case, mirroring the comment composer's
 * rollback-on-failure. You can't remove yourself here (the locked "you" chip).
 */
function ParticipantsPanel({ detail }: { detail: CaseDetail }) {
  const queryClient = useQueryClient()
  const selfEmail = useSessionStore((s) => s.contact?.email ?? '')
  const detailKey = ['case', detail.id] as const
  const participants = detail.participants ?? []

  const contactsQuery = useQuery({
    queryKey: ['account-contacts'],
    queryFn: () => api.listAccountContacts(),
    staleTime: 5 * 60_000,
  })

  const setParticipants = (next: (list: CaseParticipant[]) => CaseParticipant[]) =>
    queryClient.setQueryData<CaseDetail>(detailKey, (old) =>
      old ? { ...old, participants: next(old.participants ?? []) } : old,
    )

  const addMutation = useMutation({
    mutationFn: (contactId: string) => api.addParticipant(detail.id, contactId),
    onSuccess: (res) =>
      setParticipants((list) =>
        list.some((p) => p.contactId === res.participant.contactId) ? list : [...list, res.participant],
      ),
    onError: () => toast.error('Couldn’t add that colleague. Give it another try.'),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: detailKey }),
  })

  const removeMutation = useMutation({
    mutationFn: (contactId: string) => api.removeParticipant(detail.id, contactId),
    onMutate: (contactId) => {
      const previous = queryClient.getQueryData<CaseDetail>(detailKey)
      setParticipants((list) => list.filter((p) => p.contactId !== contactId))
      return { previous }
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(detailKey, ctx.previous)
      toast.error('Couldn’t remove that colleague. Give it another try.')
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: detailKey }),
  })

  const busy = addMutation.isPending || removeMutation.isPending

  return (
    <section aria-labelledby="case-participants-heading">
      <h2 id="case-participants-heading" className="micro-label">
        Participants
      </h2>
      <div className="mt-2.5">
        <ParticipantPicker
          contacts={contactsQuery.data?.contacts ?? []}
          participants={participants}
          onAdd={(id) => addMutation.mutate(id)}
          onRemove={(id) => removeMutation.mutate(id)}
          lockedEmail={selfEmail}
          busy={busy}
        />
      </div>
    </section>
  )
}

const localTime = (iso: string) =>
  new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso))

/**
 * Schedule a Case Escalation Call on the owner's calendar. Shows the upcoming
 * call (join / reschedule / cancel) or a "Schedule a call" button that opens the
 * tz-aware picker. Hidden for queue-owned cases (no personal calendar yet). The
 * request-a-call email lands here with ?book=1, which auto-opens the picker.
 */
function ScheduleCallPanel({ detail }: { detail: CaseDetail }) {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const isQueue = detail.owner.isQueue
  // The request-a-call email lands on /cases/:id?book=1 — open the picker at
  // mount from that flag (read once by the initializer).
  const [dialog, setDialog] = useState<'new' | 'reschedule' | null>(() =>
    searchParams.get('book') === '1' ? 'new' : null,
  )

  const bookingQuery = useQuery({
    queryKey: ['case-booking', detail.id],
    queryFn: ({ signal }) => api.getCaseBooking(detail.id, { signal }),
    staleTime: 30_000,
    enabled: !isQueue,
  })
  const booking = bookingQuery.data?.booking ?? null

  const cancelMutation = useMutation({
    mutationFn: () => api.cancelBooking(detail.id, booking!.ref),
    onSuccess: () => {
      toast.success('Your call was canceled.')
      void queryClient.invalidateQueries({ queryKey: ['case-booking', detail.id] })
    },
    onError: () => toast.error('Couldn’t cancel the call. Give it another try.'),
  })

  if (isQueue) return null

  return (
    <section aria-labelledby="case-schedule-heading">
      <h2 id="case-schedule-heading" className="micro-label">
        Call
      </h2>
      <div className="mt-2.5">
        {booking ? (
          <div className="rounded-lg border border-rule/60 bg-paper/60 px-3.5 py-3">
            <p className="text-sm font-medium text-ink">{localTime(booking.startUtc)}</p>
            <p className="mt-0.5 font-mono text-[0.6875rem] text-mute">with {detail.owner.name}</p>
            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              {booking.joinUrl ? (
                <a
                  href={booking.joinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-sm text-sm font-semibold text-crimson underline-offset-4 hover:underline"
                >
                  <ExternalLink aria-hidden="true" className="size-3.5" />
                  Join
                </a>
              ) : null}
              <button
                type="button"
                onClick={() => setDialog('reschedule')}
                className="rounded-sm text-sm text-inkMid underline-offset-4 hover:text-crimson hover:underline"
              >
                Reschedule
              </button>
              <button
                type="button"
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending}
                className="rounded-sm text-sm text-inkMid underline-offset-4 hover:text-crimson hover:underline disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <Button variant="neutral" size="sm" onClick={() => setDialog('new')}>
            <CalendarClock aria-hidden="true" />
            Schedule a call
          </Button>
        )}
      </div>

      {dialog ? (
        <ScheduleCallDialog
          caseId={detail.id}
          booking={dialog === 'reschedule' ? booking : null}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </section>
  )
}

/**
 * "Mark as resolved" ships inside the frozen contract: it posts a fixed,
 * parseable note through the normal comments endpoint. The team still closes
 * the case in Salesforce; agents and Nexus see the marker up front.
 */
const RESOLVED_COMMENT = '[Client marks resolved] Resolved on our side — please close this case.'

function ResolveRow({ caseId }: { caseId: string }) {
  const [confirming, setConfirming] = useState(false)
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => api.addComment(caseId, RESOLVED_COMMENT),
    onSuccess: (result) => {
      queryClient.setQueryData<CaseDetail>(['case', caseId], (old) =>
        old
          ? { ...old, lastActivityAt: result.entry.at, timeline: [...old.timeline, result.entry] }
          : old,
      )
      setConfirming(false)
      toast('Noted — we’ll close it out.')
    },
    onError: () => toast.error('That didn’t send. Give it another try.'),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['case', caseId] })
      void queryClient.invalidateQueries({ queryKey: ['cases'] })
    },
  })

  return (
    <div className="mt-5 flex min-h-14 flex-wrap items-center justify-between gap-3 rounded-lg border border-rule/60 bg-paper/60 px-4 py-3">
      {confirming ? (
        <>
          <p className="text-sm text-inkMid">Post a note asking us to close this case?</p>
          <div className="flex gap-2">
            <Button size="sm" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
              {mutation.isPending ? 'Posting…' : 'Post the note'}
            </Button>
            <Button
              variant="quiet"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => setConfirming(false)}
            >
              Keep it open
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-inkMid">All set on your side?</p>
          <Button variant="neutral" size="sm" onClick={() => setConfirming(true)}>
            <CircleCheck aria-hidden="true" />
            Mark as resolved
          </Button>
        </>
      )}
    </div>
  )
}

function MetaDot() {
  return (
    <span aria-hidden="true" className="text-muteSoft">
      ·
    </span>
  )
}

function CaseDescription({ markdown }: { markdown: string }) {
  const html = useMemo(() => renderMarkdown(markdown), [markdown])
  return (
    <div
      className="rich-text mt-2.5 max-w-prose text-[0.9375rem] leading-relaxed text-inkSoft"
      // Rendered from Markdown and sanitized in renderMarkdown (DOMPurify).
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
